import React, { useState } from "react";
import { MapPin, Mail, Phone, Globe, MessageCircle, Facebook, Linkedin, Youtube, Instagram, Send } from "lucide-react";
import { company } from "../mock";
import Reveal from "./Reveal";
import { toast } from "../hooks/use-toast";
import { createEnquiry } from "../api";

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please fill your name and email", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await createEnquiry(form);
      toast({ title: "Enquiry received!", description: "Thanks " + form.name + ", our team will reach out shortly." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast({ title: "Could not send enquiry", description: "Please check your email and try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const mapQuery = encodeURIComponent("Bhawani Kitchen Equipment, Lobpsons Industries, Vasai Phata, Vasai East, Maharashtra 401208");
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

  const socials = [
    { Icon: Facebook, href: company.social.facebook },
    { Icon: Youtube, href: company.social.youtube },
    { Icon: Instagram, href: company.social.instagram },
  ].filter((s) => s.href && s.href !== "#");

  return (
    <footer id="contact" className="bg-[color:var(--brand-navy)] text-slate-300">
      {/* Contact / enquiry band */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <p className="overline text-sm mb-1">Get in touch</p>
          <h2 className="font-head font-bold text-white text-3xl md:text-4xl mb-3">Contact Us</h2>
          <p className="text-cyan-200 font-medium mb-6">{company.tagline}</p>
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <MapPin className="text-cyan-300 shrink-0" size={20} />
              <span>{company.address}</span>
            </div>
            <a href={`https://wa.me/${company.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition-colors">
              <MessageCircle className="text-cyan-300 shrink-0" size={20} /> WhatsApp: {company.whatsapp}
            </a>
            <div className="flex items-start gap-3">
              <Phone className="text-cyan-300 shrink-0 mt-0.5" size={20} />
              <div className="flex flex-wrap gap-x-2">
                <span className="text-slate-400">Call:</span>
                {company.phones.map((p, i) => (
                  <React.Fragment key={p}>
                    <a href={`tel:${p}`} className="hover:text-white transition-colors">{p}</a>
                    {i < company.phones.length - 1 && <span className="text-slate-500">/</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <a href={`mailto:${company.email}`} className="flex items-center gap-3 hover:text-white transition-colors">
              <Mail className="text-cyan-300 shrink-0" size={20} /> {company.email}
            </a>
            <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition-colors">
              <Globe className="text-cyan-300 shrink-0" size={20} /> {company.website}
            </a>
          </div>
          <div className="flex gap-3 mt-8">
            {socials.map(({ Icon, href }, i) => (
              <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[color:var(--brand-cyan)] flex items-center justify-center transition-colors">
                <Icon size={18} className="text-white" />
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-sm">
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-slate-400 outline-none focus:border-cyan-300 transition-colors"
              />
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-slate-400 outline-none focus:border-cyan-300 transition-colors"
              />
            </div>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              placeholder="Tell us about your requirement..."
              className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-slate-400 outline-none focus:border-cyan-300 transition-colors mb-4 resize-none"
            />
            <button type="submit" disabled={sending} className="brand-btn w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold disabled:opacity-70">
              {sending ? "Sending..." : "Send Enquiry"} <Send size={17} />
            </button>
          </form>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-head font-semibold text-white text-xl flex items-center gap-2">
            <MapPin className="text-cyan-300" size={20} /> Find Us
          </h3>
          <a href={mapLink} target="_blank" rel="noopener noreferrer" data-testid="map-directions-link" className="text-sm font-medium text-cyan-200 hover:text-white transition-colors">
            Get Directions &rarr;
          </a>
        </div>
        <div className="rounded-2xl overflow-hidden border border-white/10 h-[320px] md:h-[400px]">
          <iframe
            title="Bhawani Kitchen Equipment location"
            src={`https://maps.google.com/maps?q=${mapQuery}&z=15&output=embed`}
            className="w-full h-full border-0"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-google-map"
          />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href="#blogs" className="hover:text-white transition-colors">Blog</a>
            <a href="#" className="hover:text-white transition-colors">Careers</a>
            <a href="#" className="hover:text-white transition-colors">Services Network</a>
            <a href="#" className="hover:text-white transition-colors">No Gift Policy</a>
            <a href="/admin" className="hover:text-white transition-colors">Admin</a>
          </div>
          <p>Copyright &copy; {new Date().getFullYear()} {company.name}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
